
import { computed, defineComponent, nextTick, onMounted, ref } from 'vue'
import { useForm, useValidateField } from 'vee-validate'
import { APIClient } from '@/lib/api-client'
import { Campaigns } from '@/lib/campaigns'
import { Campaign, OrderResult, PurchaserAddress, CreditCard } from '@/types'
import { Selection, ShippingAddress, Payment, ZipCodeSearchResult } from '@/types/order'

import SuzuriHeader from '@/components/SuzuriHeader.vue'
import FlashMessage from '@/components/FlashMessage.vue'
import OrderForm from '@/components/OrderForm.vue'
import OrderConfirm from '@/components/OrderConfirm.vue'
import { useStore } from '@/store'

interface Card {
  cardno: string;
  expire: string;
  securitycode: string;
}

interface EpsilonToken {
  init: (constractCode: string) => void;
  getToken: (card: Card, jsonpCallback: string) => void;
}

interface TokenResponse {
  resultCode: string;
  tokenObject: {
    token: string;
    maskedCardNo: string;
    cardExpire: string;
    isSecurityCodeSet: string;
    toBeExpiredAt: string;
  }
}

declare global {
  interface Window {
    EpsilonToken: EpsilonToken;
    epsilonCallback: (response: TokenResponse) => void;
  }
}

interface PaymentParams {
  expire?: string;
  token?: string;
  remember: boolean;
  continuedUse: boolean;
}

export default defineComponent({
  components: {
    SuzuriHeader,
    FlashMessage,
    OrderForm,
    OrderConfirm
  },
  props: {
    epsilonJsUrl: {
      type: String,
      required: true
    },
    epsilonContractCode: {
      type: String,
      required: true
    },
    flashMessage: {
      type: String,
      default: null
    }
  },
  setup (props) {
    const store = useStore()

    const { validate } = useForm()
    const validateAddressField = useValidateField('address1')

    const campaign = ref<Campaign | null>(null)
    const selections = ref<Selection[]>([])
    const useRegisteredAddress = ref(false)
    const useRegisteredCreditCard = ref(false)
    const shippingAddress = ref<ShippingAddress>({
      lastName: '',
      firstName: '',
      zipCode: '',
      prefectureCode: '01',
      address1: '',
      address2: '',
      address3: '',
      phone: '',
      purchaserEmail: '',
      remember: true
    })
    const payment = ref<Payment>({
      method: 'creditCard',
      cardNumber: '',
      cardExpiration: '',
      cardSecurityCode: '',
      cardRemember: true
    })
    const registeredCreditCard = ref<CreditCard | null>(null)
    const confirm = ref(false)
    const submitting = ref(false)
    const epsilonJs = ref<HTMLDivElement>()

    const campaignId = computed((): number => {
      const id = location.pathname.match('campaigns/(\\d+)/orders')
      return id ? +id[1] : 0
    })

    const campaignPageUrl = computed((): string => {
      return `/squeegee/campaigns/${campaignId.value}`
    })

    const totalQuantity = computed((): number => {
      return selections.value.reduce((sum, selection) => sum + selection.quantity, 0)
    })

    const submitLabel = computed((): string => {
      return campaign.value && campaign.value.sample ? 'サンプル発注する' : 'この内容で予約する'
    })

    onMounted(async () => {
      window.addEventListener('beforeunload', handleBeforeunload)

      // 3Dセキュア認証に失敗した場合、Railsのflashメッセージを付けてこのページにリダイレクトされる
      // フロントエンドではpropsとしてメッセージを受け取り、ここで表示する
      if (props.flashMessage) {
        store.dispatch('showErrorMessage', props.flashMessage)
      }

      await Promise.all([fetchRegisteredAddress(), fetchRegisteredCreditCard()])

      campaign.value = await APIClient.get<Campaign>(`campaigns/${campaignId.value}`).then(res => res.data)

      if (campaign.value) {
        const primarySize = Campaigns.primarySize(campaign.value)

        const query = new URLSearchParams(location.search)
        const sizeId = query.get('sizeId')
        const itemSizeId = sizeId ? +sizeId : primarySize.id
        const variantId = query.get('variantId')
        const variant = variantId ? Campaigns.variant(campaign.value, +variantId) : campaign.value.variants[0]

        selections.value = variant ? [{ variantId: variant.id, itemSizeId, quantity: 1 }] : []

        if (campaign.value.sample) {
          payment.value.method = 'sampleOrder'
        }
      }

      loadEpsilonScript()
    })

    const proceedToConfirm = async () => {
      let isValid = (await validate()).valid
      let message = ''

      if (campaign.value && campaign.value.sample && totalQuantity.value > 3) {
        isValid = false
        message = 'サンプル発注できる数量は3点までです。'
      }

      if (isValid) {
        confirm.value = true
        window.scrollTo(0, 0)
      } else {
        const invalidInput = document.querySelector('.invalid')
        invalidInput?.scrollIntoView({ behavior: 'smooth', block: 'center' })
        if (message.length) { store.dispatch('showMessage', message) }
      }
    }

    const handleBeforeunload = (event: BeforeUnloadEvent) => {
      event.preventDefault()
      event.returnValue = ''
    }

    const fetchRegisteredAddress = async () => {
      try {
        const registeredAddress = await APIClient.get<PurchaserAddress>('purchaser_address').then(res => res.data)

        shippingAddress.value = {
          ...registeredAddress,
          purchaserEmail: registeredAddress.email,
          remember: false
        }
        useRegisteredAddress.value = true
      } catch {
        useRegisteredAddress.value = false
      }
    }

    const fetchRegisteredCreditCard = async () => {
      try {
        registeredCreditCard.value = await APIClient.get<CreditCard>('credit_card').then(res => res.data)
        useRegisteredCreditCard.value = true
      } catch {
        registeredCreditCard.value = null
        useRegisteredCreditCard.value = false
      }
    }

    const loadEpsilonScript = async () => {
      const container = epsilonJs.value as HTMLDivElement
      const child = document.createElement('script')

      child.setAttribute('src', props.epsilonJsUrl)
      container.appendChild(child)
    }

    const handleSelectionsChange = async (sels: Selection[]) => {
      selections.value = sels
    }

    const handleAddressInput = async (address: ShippingAddress) => {
      const zipCode = address.zipCode.replace('-', '')
      const zipCodeDiff = zipCode !== shippingAddress.value.zipCode.replace('-', '')

      shippingAddress.value = address

      if (zipCodeDiff && zipCode.length === 7 && address.address1.trim().length === 0) {
        const url = `/shopping/zipcode/search?code=${zipCode}`

        try {
          const res: ZipCodeSearchResult = await window.fetch(url).then(res => res.json())

          if (res.code === 200 && res.locations.length > 0) {
            shippingAddress.value.prefectureCode = res.locations[0].prefectureCode
            shippingAddress.value.address1 = res.locations[0].address

            // Suggestion
            shippingAddress.value.suggestion = {
              prefectureCode: res.locations[0].prefectureCode,
              addresses: res.locations.map((l) => l.address)
            }
            nextTick(validateAddressField)
          }
        } catch (e) {
          // 住所の自動補完に失敗してもなにもしない
        }
      }
    }

    const handlePaymentInput = (p: Payment) => {
      payment.value = p
    }

    const handleEditRegisteredCard = () => {
      useRegisteredCreditCard.value = false
    }

    const backToInput = () => {
      confirm.value = false
      window.scrollTo(0, 0)
    }

    const handleSubmit = async () => {
      if (submitting.value) { return }

      submitting.value = true

      if (payment.value.method === 'creditCard') {
        if (useRegisteredCreditCard.value) {
          try {
            await submitOrder({
              remember: false,
              continuedUse: true
            })
          } finally {
            submitting.value = false
          }
        } else {
          try {
            const response = await fetchEpsilonToken()

            if (!response) { return }

            if (response.resultCode === '000') {
              await submitOrder({
                expire: response.tokenObject.cardExpire,
                token: response.tokenObject.token,
                remember: payment.value.cardRemember,
                continuedUse: false
              })
            } else {
              const resultCodeNum = parseInt(response.resultCode, 10)
              const errorMessages: Record<number, string> = {
                101: 'カード番号が不正です。',
                102: 'カード番号が不正です。',
                111: '入力された有効期限が不正です。MM/YYの形式で入力してください。',
                113: '入力された有効期限が不正です。MM/YYの形式で入力してください。'
              } // ref: https://mp-faq.gmo-pg.com/s/article/D00908
              const errorMessage = errorMessages[resultCodeNum] || `決済処理でエラーが発生しました(エラーコード: ${response.resultCode})`
              store.dispatch('showErrorMessage', errorMessage)
            }
          } catch (e) {
            console.log('EpsilonToken is not ready')
          } finally {
            submitting.value = false
          }
        }
      } else if (payment.value.method === 'sampleOrder') {
        try {
          await submitOrder()
        } finally {
          submitting.value = false
        }
      } else {
        // 後払い
        // await this.submitOrder(params)
      }
    }

    const fetchEpsilonToken = (): Promise<TokenResponse> => {
      if (window.EpsilonToken) {
        const cardObj = {
          cardno: payment.value.cardNumber.replace(/-/g, ''),
          expire: '20' + payment.value.cardExpiration.split('/').reverse().join(''),
          securitycode: payment.value.cardSecurityCode
        }

        return new Promise<TokenResponse>(resolve => {
          window.epsilonCallback = (response: TokenResponse) => resolve(response)

          window.EpsilonToken.init(props.epsilonContractCode)
          window.EpsilonToken.getToken(cardObj, 'window.epsilonCallback')
        })
      } else {
        return new Promise((resolve, reject) => reject(new Error())) // TODO: Use Custom Error
      }
    }

    const submitOrder = async (paymentParams?: PaymentParams) => {
      if (!campaign.value) { return }

      const order = {
        details: selections.value.map(s => ({
          variantId: s.variantId,
          itemSizeId: s.itemSizeId,
          quantity: s.quantity
        })),
        shippingAddress: {
          firstName: shippingAddress.value.firstName,
          lastName: shippingAddress.value.lastName,
          zipCode: shippingAddress.value.zipCode.replace('-', ''),
          prefectureCode: shippingAddress.value.prefectureCode,
          address1: shippingAddress.value.address1,
          address2: shippingAddress.value.address2,
          address3: shippingAddress.value.address3,
          phone: shippingAddress.value.phone,
          remember: shippingAddress.value.remember
        },
        purchaserEmail: shippingAddress.value.purchaserEmail,
        paymentMethod: payment.value.method,
        payment: paymentParams
      }

      try {
        const orderResult = await APIClient.post<OrderResult>(`/campaigns/${campaign.value.id}/orders`, { order }).then(res => res.data)
        window.removeEventListener('beforeunload', handleBeforeunload)

        if (orderResult.result === 'three_d_secure') {
          if (!orderResult.threeDSecureUrl || !orderResult.paReq || !orderResult.termUrl) {
            throw new Error('3Dセキュアの情報取得に失敗しました。再度お試しください')
          }

          // 「（２）購入者様リダイレクト時必要パラメータ」の仕様にしたがったフォームを作って送信する
          // https://www.epsilon.jp/developer/3dsecure2.html
          const form = document.createElement('form')
          form.method = 'POST'
          form.action = orderResult.threeDSecureUrl

          const paReqInput = document.createElement('input')
          paReqInput.type = 'hidden'
          paReqInput.name = 'PaReq'
          paReqInput.value = orderResult.paReq

          const termUrlInput = document.createElement('input')
          termUrlInput.type = 'hidden'
          termUrlInput.name = 'TermUrl'
          termUrlInput.value = orderResult.termUrl

          form.appendChild(paReqInput)
          form.appendChild(termUrlInput)
          document.body.appendChild(form)
          form.submit()
        } else {
          location.href = `/squeegee/campaigns/${campaign.value.id}/orders/${orderResult.order.id}`
        }
      } catch (e) {
        store.dispatch('showAPIError', e)
      } finally {
        submitting.value = false
      }
    }

    return {
      campaign,
      confirm,
      selections,
      shippingAddress,
      payment,
      useRegisteredCreditCard,
      useRegisteredAddress,
      registeredCreditCard,
      handleSelectionsChange,
      handleAddressInput,
      handlePaymentInput,
      handleEditRegisteredCard,
      epsilonJs,
      submitting,
      submitLabel,
      handleSubmit,
      backToInput,
      campaignPageUrl,
      proceedToConfirm
    }
  }
})
